import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Share2, Download, X, Loader2 } from "lucide-react";

const CATEGORY_COLORS = {
  Physique: "#7fb3ff", Chimie: "#8affd1", Biologie: "#a0f0a0", Mathématiques: "#f0c674",
  Histoire: "#e0a878", Philosophie: "#c9a0ff", Art: "#ff9ecd", Littérature: "#ffd29e",
  Astronomie: "#9ecbff", Géographie: "#7fe0c0", Musique: "#e0a0ff", Technologie: "#a0c0ff",
  Langues: "#ffb0b0", Économie: "#c0e080", Logique: "#ff9d5c", Autre: "#e6d3a3",
};
const colorFor = (c) => CATEGORY_COLORS[c] || CATEGORY_COLORS.Autre;

const T = {
  fr: { button: "Partager ma constellation", title: "Ma Constellation",
    tagline: "Chaque étoile, une curiosité éveillée.", download: "Télécharger", share: "Partager",
    stats: (s, l) => `${s} étoiles · ${l} liens`, generating: "Je dessine ton ciel…",
    shareText: "Voici ma constellation de curiosités sur Cocoon Mind 🌌 Crée la tienne sur cocoonmind.com",
    dlOk: "Image enregistrée 🌌", shareErr: "Partage impossible ici — image téléchargée à la place.",
    fmtSquare: "Carré", fmtStory: "Story", fmtHint: "Story (9:16) — parfait pour TikTok & Reels" },
  en: { button: "Share my constellation", title: "My Constellation",
    tagline: "Every star, a spark of curiosity.", download: "Download", share: "Share",
    stats: (s, l) => `${s} stars · ${l} links`, generating: "Drawing your sky…",
    shareText: "Here is my constellation of curiosities on Cocoon Mind 🌌 Create yours at cocoonmind.com",
    dlOk: "Image saved 🌌", shareErr: "Sharing unavailable here — image downloaded instead.",
    fmtSquare: "Square", fmtStory: "Story", fmtHint: "Story (9:16) — perfect for TikTok & Reels" },
  es: { button: "Compartir mi constelación", title: "Mi Constelación",
    tagline: "Cada estrella, una curiosidad despierta.", download: "Descargar", share: "Compartir",
    stats: (s, l) => `${s} estrellas · ${l} enlaces`, generating: "Dibujando tu cielo…",
    shareText: "Esta es mi constelación de curiosidades en Cocoon Mind 🌌 Crea la tuya en cocoonmind.com",
    dlOk: "Imagen guardada 🌌", shareErr: "No se puede compartir aquí — imagen descargada.",
    fmtSquare: "Cuadrado", fmtStory: "Story", fmtHint: "Story (9:16) — perfecto para TikTok y Reels" },
  nl: { button: "Mijn constellatie delen", title: "Mijn Constellatie",
    tagline: "Elke ster, een gewekte nieuwsgierigheid.", download: "Downloaden", share: "Delen",
    stats: (s, l) => `${s} sterren · ${l} verbindingen`, generating: "Je hemel tekenen…",
    shareText: "Dit is mijn constellatie van nieuwsgierigheid op Cocoon Mind 🌌 Maak de jouwe op cocoonmind.com",
    dlOk: "Afbeelding opgeslagen 🌌", shareErr: "Delen lukt hier niet — afbeelding gedownload.",
    fmtSquare: "Vierkant", fmtStory: "Story", fmtHint: "Story (9:16) — perfect voor TikTok & Reels" },
};

// deterministic PRNG
function mulberry32(seed) {
  let t = seed >>> 0;
  return function () {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}
function hashStr(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}

function computePositions(nodes, cx, cy, innerR, outerR) {
  const byCat = {};
  nodes.forEach((n) => { const c = (n.category || "Autre").trim() || "Autre"; (byCat[c] = byCat[c] || []).push(n); });
  const cats = Object.keys(byCat).sort();
  const pos = {};
  const N = Math.max(cats.length, 1);
  cats.forEach((cat, ci) => {
    const list = byCat[cat].slice().sort((a, b) => (b.count || 1) - (a.count || 1));
    const baseAngle = (ci / N) * Math.PI * 2 - Math.PI / 2;
    list.forEach((n, j) => {
      if (n.is_cover && !pos[n.name]) { pos[n.name] = { x: cx, y: cy, cover: true, node: n }; return; }
      const rnd = mulberry32(hashStr(n.name + cat));
      const spread = (Math.PI * 2) / N * 0.72;
      const angle = baseAngle + (rnd() - 0.5) * spread;
      const frac = (j + 1) / (list.length + 1);
      const radius = innerR + frac * (outerR - innerR) + (rnd() - 0.5) * 40;
      pos[n.name] = { x: cx + Math.cos(angle) * radius, y: cy + Math.sin(angle) * radius, cover: false, node: n };
    });
  });
  return pos;
}

function layoutFor(format) {
  if (format === "story") {
    return { W: 1080, H: 1920, cy: 1090, innerR: 140, outerR: 500,
      logoTop: 70, logoH: 152,
      wordY: 300, mindY: 334, titleY: 430, statsY: 482,
      wordSize: 54, mindSize: 24, titleSize: 92, statsSize: 30,
      tagFromBottom: 168, urlFromBottom: 104, tagSize: 40, urlSize: 26,
      labelMax: 10, dust: 300 };
  }
  return { W: 1080, H: 1080, cy: 600, innerR: 100, outerR: 415,
    logoTop: 44, logoH: 96,
    wordY: 190, mindY: 218, titleY: 300, statsY: 340,
    wordSize: 46, mindSize: 20, titleSize: 78, statsSize: 26,
    tagFromBottom: 78, urlFromBottom: 40, tagSize: 34, urlSize: 22,
    labelMax: 8, dust: 160 };
}

// Logo officiel préchargé (même origine → canvas non contaminé)
let _logoImg = null, _logoPromise = null;
function loadLogo() {
  if (_logoImg) return Promise.resolve(_logoImg);
  if (_logoPromise) return _logoPromise;
  _logoPromise = new Promise((res) => {
    const im = new Image();
    im.onload = () => { _logoImg = im; res(im); };
    im.onerror = () => res(null);
    im.src = "/cocoon-logo-glow.png";
  });
  return _logoPromise;
}

async function drawConstellation(canvas, nodes, links, L, lang, format = "square") {
  const cfg = layoutFor(format);
  const W = cfg.W, H = cfg.H;
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  const cx = W / 2, cy = cfg.cy;

  // background
  const bg = ctx.createRadialGradient(cx, cy, 60, cx, cy, Math.max(W, H) * 0.72);
  bg.addColorStop(0, "#1a1207");
  bg.addColorStop(0.45, "#0e0a06");
  bg.addColorStop(1, "#050403");
  ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

  // star dust
  const dust = mulberry32(1337);
  for (let i = 0; i < cfg.dust; i++) {
    const x = dust() * W, y = dust() * H, r = dust() * 1.4 + 0.2;
    ctx.globalAlpha = 0.10 + dust() * 0.25;
    ctx.fillStyle = "#ffe9c8";
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
  }
  ctx.globalAlpha = 1;

  const pos = computePositions(nodes, cx, cy, cfg.innerR, cfg.outerR);

  // links
  ctx.lineWidth = 1;
  links.forEach((e) => {
    const a = pos[e.source], b = pos[e.target];
    if (!a || !b) return;
    const grad = ctx.createLinearGradient(a.x, a.y, b.x, b.y);
    grad.addColorStop(0, colorFor(a.node.category) + "55");
    grad.addColorStop(1, colorFor(b.node.category) + "22");
    ctx.strokeStyle = grad; ctx.globalAlpha = 0.5;
    ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
  });
  ctx.globalAlpha = 1;

  // nodes
  Object.values(pos).forEach((p) => {
    const c = colorFor(p.node.category);
    const count = p.node.count || 1;
    const r = p.cover ? 13 : Math.min(5 + count * 1.6, 12);
    ctx.shadowColor = c; ctx.shadowBlur = p.cover ? 34 : 18;
    ctx.fillStyle = c;
    ctx.beginPath(); ctx.arc(p.x, p.y, r, 0, Math.PI * 2); ctx.fill();
    ctx.shadowBlur = 0;
    ctx.fillStyle = "#fff8ee";
    ctx.beginPath(); ctx.arc(p.x, p.y, Math.max(r * 0.35, 1.6), 0, Math.PI * 2); ctx.fill();
  });

  // labels — top stars by count, with collision avoidance
  const candidates = Object.values(pos)
    .sort((a, b) => (b.node.count || 1) - (a.node.count || 1))
    .slice(0, 18);
  ctx.font = "500 22px 'Manrope', sans-serif";
  ctx.textBaseline = "middle";
  const placed = [];
  const lh = 36;
  for (const p of candidates) {
    if (placed.length >= cfg.labelMax) break;
    const name = (p.node.name || "").slice(0, 24);
    const w = ctx.measureText(name).width;
    const left = p.x > cx;
    const tx = left ? p.x + 16 : p.x - 16 - w;
    const box = { x: tx - 5, y: p.y - lh / 2, w: w + 10, h: lh };
    const overlap = placed.some((q) => !(box.x > q.x + q.w || box.x + box.w < q.x || box.y > q.y + q.h || box.y + box.h < q.y));
    if (overlap) continue;
    placed.push(box);
    ctx.fillStyle = "rgba(255,240,220,0.78)";
    ctx.textAlign = "left";
    ctx.fillText(name, tx, p.y);
  }

  // header wordmark
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  const logo = await loadLogo();
  if (logo && logo.height) {
    const lw = cfg.logoH * (logo.width / logo.height);
    ctx.save();
    ctx.shadowColor = "rgba(240,155,45,0.5)"; ctx.shadowBlur = 40;
    ctx.drawImage(logo, cx - lw / 2, cfg.logoTop, lw, cfg.logoH);
    ctx.restore();
  }
  const amber = ctx.createLinearGradient(cx - 200, 0, cx + 200, 0);
  amber.addColorStop(0, "#f7b955"); amber.addColorStop(1, "#e8822b");
  ctx.fillStyle = amber;
  ctx.font = `600 ${cfg.wordSize}px 'Cormorant Garamond', serif`;
  drawTracked(ctx, "COCOON", cx, cfg.wordY, 10);
  ctx.fillStyle = "rgba(240,200,150,0.65)";
  ctx.font = `500 ${cfg.mindSize}px 'Manrope', sans-serif`;
  drawTracked(ctx, "M I N D", cx, cfg.mindY, 6);

  // title
  ctx.fillStyle = amber;
  ctx.font = `italic 600 ${cfg.titleSize}px 'Cormorant Garamond', serif`;
  ctx.fillText(L.title, cx, cfg.titleY);

  // stats
  ctx.fillStyle = "rgba(255,235,205,0.7)";
  ctx.font = `500 ${cfg.statsSize}px 'Manrope', sans-serif`;
  ctx.fillText(L.stats(nodes.length, links.length), cx, cfg.statsY);

  // footer tagline + url
  ctx.fillStyle = "rgba(240,205,160,0.85)";
  ctx.font = `italic 500 ${cfg.tagSize}px 'Cormorant Garamond', serif`;
  ctx.fillText(L.tagline, cx, H - cfg.tagFromBottom);
  ctx.fillStyle = "rgba(255,235,205,0.5)";
  ctx.font = `500 ${cfg.urlSize}px 'Manrope', sans-serif`;
  drawTracked(ctx, "cocoonmind.com", cx, H - cfg.urlFromBottom, 3);
}

function drawTracked(ctx, text, cx, y, spacing) {
  const widths = [...text].map((ch) => ctx.measureText(ch).width + spacing);
  const total = widths.reduce((a, b) => a + b, 0) - spacing;
  let x = cx - total / 2;
  const prevAlign = ctx.textAlign;
  ctx.textAlign = "left";
  [...text].forEach((ch, i) => { ctx.fillText(ch, x, y); x += widths[i]; });
  ctx.textAlign = prevAlign;
}

export function ConstellationShareButton({ nodes, links }) {
  const { i18n } = useTranslation();
  const L = T[(i18n.language || "fr").slice(0, 2)] || T.fr;
  const canvasRef = useRef(document.createElement("canvas"));
  const blobRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [format, setFormat] = useState("story");

  const render = async (fmt) => {
    setBusy(true);
    try {
      if (document.fonts && document.fonts.ready) { try { await document.fonts.ready; } catch { /* noop */ } }
      await drawConstellation(canvasRef.current, nodes, links, L, i18n.language, fmt);
      await new Promise((res) => canvasRef.current.toBlob((b) => {
        blobRef.current = b;
        setUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(b); });
        res();
      }, "image/png"));
      setOpen(true);
    } catch (e) {
      toast.error(L.shareErr);
    } finally { setBusy(false); }
  };

  const generate = () => render(format);

  const switchFormat = (fmt) => {
    if (fmt === format || busy) return;
    setFormat(fmt);
    render(fmt);
  };

  const download = () => {
    const a = document.createElement("a");
    a.href = url; a.download = `ma-constellation-cocoon-${format}.png`; a.click();
    toast.success(L.dlOk);
  };

  const share = async () => {
    const blob = blobRef.current;
    if (!blob) return;
    const file = new File([blob], `ma-constellation-cocoon-${format}.png`, { type: "image/png" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: L.title, text: L.shareText });
        return;
      } catch (e) {
        if (e && e.name === "AbortError") return;
      }
    }
    download();
    toast(L.shareErr);
  };

  return (
    <>
      <button
        onClick={generate}
        disabled={busy || !nodes?.length}
        data-testid="constellation-share-btn"
        className="btn-frost-orange inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm disabled:opacity-50"
      >
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />}
        {busy ? L.generating : L.button}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          data-testid="constellation-share-modal"
          onClick={() => setOpen(false)}
        >
          <div className="relative max-w-md w-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setOpen(false)}
              data-testid="constellation-share-close"
              className="absolute -top-3 -right-3 z-10 w-10 h-10 rounded-full bg-background border border-white/15 flex items-center justify-center text-foreground/70 hover:text-primary"
            >
              <X size={18} />
            </button>

            <div className="mb-3 inline-flex items-center p-1 rounded-full bg-background/70 border border-white/10" data-testid="constellation-format-toggle">
              <button
                data-testid="constellation-format-story"
                onClick={() => switchFormat("story")}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${format === "story" ? "bg-primary text-primary-foreground" : "text-foreground/60 hover:text-foreground"}`}
              >
                {L.fmtStory} · 9:16
              </button>
              <button
                data-testid="constellation-format-square"
                onClick={() => switchFormat("square")}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${format === "square" ? "bg-primary text-primary-foreground" : "text-foreground/60 hover:text-foreground"}`}
              >
                {L.fmtSquare} · 1:1
              </button>
            </div>

            <div className="relative w-full flex justify-center">
              {busy && (
                <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-black/50">
                  <Loader2 size={28} className="animate-spin text-primary" />
                </div>
              )}
              {url && <img src={url} alt={L.title} data-testid="constellation-share-preview" className="max-h-[62vh] w-auto rounded-2xl border border-primary/25 shadow-2xl" />}
            </div>

            {format === "story" && <p className="mt-2 text-xs text-primary/70">{L.fmtHint}</p>}

            <div className="mt-4 flex gap-3 justify-center">
              <button onClick={share} data-testid="constellation-share-action" className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm">
                <Share2 size={16} /> {L.share}
              </button>
              <button onClick={download} data-testid="constellation-download-action" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-primary/30 text-primary hover:bg-primary/10 transition-colors font-semibold text-sm">
                <Download size={16} /> {L.download}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
